
import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { VirtualTeamMember } from "../types";

interface VirtualTeamTableProps {
  virtualTeam: VirtualTeamMember[];
  isLoading: boolean;
  onEdit: (member: VirtualTeamMember) => void;
  onDelete: (id: string) => void;
  canEdit: boolean;
}

export const VirtualTeamTable = ({ virtualTeam, isLoading, onEdit, onDelete, canEdit }: VirtualTeamTableProps) => {
  const calculateEmptyRows = () => {
    const minVisibleRows = 8;
    const currentRows = virtualTeam.length;
    const emptyRowsNeeded = Math.max(minVisibleRows - currentRows, 0);
    return Array.from({ length: emptyRowsNeeded }, (_, i) => i);
  };

  if (isLoading) {
    return <div className="text-center py-4 text-white">Loading...</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-primary/30 hover:bg-primary/30">
          <TableHead className="text-primary-light text-[11px]">SN</TableHead>
          <TableHead className="text-primary-light text-[11px]">Title</TableHead>
          <TableHead className="text-primary-light text-[11px]">Name</TableHead>
          <TableHead className="text-primary-light text-[11px]">Activity</TableHead>
          <TableHead className="text-primary-light text-[11px]">Expected Completion</TableHead>
          <TableHead className="text-primary-light text-[11px]">Status</TableHead>
          <TableHead className="text-primary-light text-[11px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {virtualTeam.map((member, index) => (
          <TableRow key={member.id} className="bg-secondary-light/5">
            <TableCell className="text-white text-[11px]">{index + 1}</TableCell>
            <TableCell className="text-white text-[11px]">{member.title}</TableCell>
            <TableCell className="text-white text-[11px]">{member.name}</TableCell>
            <TableCell className="text-white text-[11px]">{member.activity}</TableCell>
            <TableCell className="text-white text-[11px]">
              {member.expected_completion_date ? new Date(member.expected_completion_date).toLocaleDateString() : ''}
            </TableCell>
            <TableCell className="text-white text-[11px]">{member.status}</TableCell>
            <TableCell className="text-white text-[11px]">
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(member)}
                  className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                  disabled={!canEdit}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(member.id)}
                  className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                  disabled={!canEdit}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {calculateEmptyRows().map(index => (
          <TableRow key={`empty-${index}`} className="bg-secondary-light/5">
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
            <TableCell className="text-white text-[11px]">&nbsp;</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
